/**
 * Find one student on a campus's roster by name — the office has the name,
 * rarely a student number. Controlled: the parent owns the picked student.
 * Shared by the special-diet intake and the order-for-a-student dialogs.
 */
import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { type ReactElement, useState } from 'react'

import {
  studentDisambiguator,
  type StudentSearchResultWire,
  useStudentSearchQuery,
} from '@/api/special-diet-accommodations'

export interface StudentRosterPickerProps {
  locationId: string
  schoolName: string
  picked: StudentSearchResultWire | null
  onPick: (student: StudentSearchResultWire | null) => void
  inputId: string
  /** Extra guidance when the campus has no roster yet. */
  emptyRosterHint?: string
  /** What the admin should confirm when two students cannot be told apart. */
  ambiguousHint: string
}

export function StudentRosterPicker(props: StudentRosterPickerProps): ReactElement {
  const [query, setQuery] = useState('')
  const search = useStudentSearchQuery({ locationId: props.locationId, search: props.picked ? '' : query })
  const settled = !search.isPending && !search.isPlaceholderData
  const matches = !settled || search.error ? [] : (search.data?.students ?? [])
  const campusHasStudents = settled ? search.data?.campusHasStudents : undefined

  if (props.picked) {
    const detail = studentDisambiguator(props.picked)
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border p-2">
        <div className="flex flex-col">
          <span className="text-sm">{props.picked.name}</span>
          {detail ? <span className="text-muted-foreground text-xs">{detail}</span> : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            props.onPick(null)
            setQuery('')
          }}>
          Change
        </Button>
      </div>
    )
  }

  return (
    <>
      <Label htmlFor={props.inputId}>Search student roster</Label>
      <Input
        id={props.inputId}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Start typing a name"
        autoComplete="off"
      />
      {search.error ? (
        <div role="alert" className="space-y-2">
          <p className="text-destructive text-xs">{search.error.message}</p>
          <Button type="button" variant="outline" onClick={() => void search.refetch()}>
            Retry
          </Button>
        </div>
      ) : campusHasStudents === false ? (
        <p className="text-muted-foreground text-xs">
          No students are rostered at {props.schoolName} yet. The roster may still be pending import.
          {props.emptyRosterHint ? ` ${props.emptyRosterHint}` : ''}
        </p>
      ) : query.trim().length >= 2 && settled && matches.length === 0 ? (
        <p className="text-muted-foreground text-xs">No student by that name at this campus. Check the spelling.</p>
      ) : null}
      {matches.length > 0 ? (
        <div className="flex max-h-48 flex-col overflow-y-auto rounded-md border">
          {matches.map((row) => (
            <button
              key={row.userId}
              type="button"
              className="hover:bg-muted flex flex-col items-start px-2 py-1.5 text-left"
              onClick={() => props.onPick(row)}>
              <span className="text-sm">{row.name}</span>
              {studentDisambiguator(row) ? (
                <span className="text-muted-foreground text-xs">{studentDisambiguator(row)}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
      {/* Two students, same name, nothing on either to tell them apart — say
          so rather than let the admin guess. */}
      {settled && search.data?.ambiguous ? (
        <span className="text-destructive text-xs">
          More than one student shares that name and neither has a grade or student number on file.{' '}
          {props.ambiguousHint}
        </span>
      ) : null}
    </>
  )
}
